import {Component, Inject, NgZone, OnInit} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {OrganizationOverview} from '../../../core/classes/organization-overview.class';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {DataBrokerService} from '../../../core/services/data-broker/data-broker.service';
import {ImageService} from '../../../core/services/image/image.service';
import {OrganizationService} from '../../../core/services/organization/organization.service';
import {OrgOverviewService} from '../../../modules/organizations/services/org-overview.service';
import {mergeMap} from 'rxjs/operators';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {LanguageService} from '../../../core/services/language/language.service';
import {Organization} from '../../../core/models/organization.model';
import {SnackbarComponent} from '../../components/snackbar/snackbar.component';
import {HttpErrorResponse} from '@angular/common/http';
import globals from '../../../../config';
import config from '../../../../config';
import {UserSessionService} from '../../../core/services/user-session/user-session.service';
import {MatSnackBar, MatSnackBarConfig} from '@angular/material/snack-bar';
import {Address} from '../../../core/classes/address.class';
import {FormCustomValidators} from '../../../core/utils/form-custom-validators';
import {CheckGoogleAddressService} from '../../../core/services/patient/check-google-address.service';
import {OrganizationTypes} from '../../enums/organization';
import {Router} from '@angular/router';
import { ClinicAccessService } from 'src/app/core/services/clinic-access/clinic-access.service';
import { environment } from 'src/environments/environment';

interface DialogData {
  mode: string;
  parentOrgId: string;
  organizationType: OrganizationTypes;
  doNotRedirectToClinicOverviewAfterAdd: boolean;
}

@UntilDestroy()
@Component({
  selector: 'app-clinic-onboard-dialog',
  templateUrl: './clinic-onboard-dialog.component.html',
  styleUrls: ['./clinic-onboard-dialog.component.scss'],
})
export class ClinicOnboardDialogComponent implements OnInit {
  clinicGroup: FormGroup;
  isSubmitted = false;

  public labels: {[key: string]: string};
  public isLoaded = false;
  public organization: OrganizationOverview;
  public selectedTab = 0;
  public tabList: Array<string> = [];
  isNewOrg = true;
  isLoading = false;
  address: Address;
  podType;
  regex =
    /^(?!.*__.*)[a-z0-9!$%*=?^_`{|}~-]+(?:\.[a-z0-9!$%*=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:[a-z]{2}|aero|arpa|asia|biz|cat|com|coop|edu|gov|info|int|jobs|mil|mobi|museum|name|net|org|pro|tel|travel)$/i;

  private snackBarConf: MatSnackBarConfig = {
    duration: 0,
    horizontalPosition: 'center',
    verticalPosition: 'top',
  };
  orgId: string;

  isEditable:boolean = true;
  checkPrimaryStoreNumber: boolean;

  constructor(
    public dialogRef: MatDialogRef<ClinicOnboardDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dataBrokerService: DataBrokerService,
    private imageService: ImageService,
    private organizationService: OrganizationService,
    private orgOverviewService: OrgOverviewService,
    private languageService: LanguageService,
    private session: UserSessionService,
    private orgService: OrganizationService,
    private snackBar: MatSnackBar,
    private fb: FormBuilder,
    private ngZone: NgZone,
    private router: Router,
    private checkGoogleAddressService: CheckGoogleAddressService,
    private clinicAccessService: ClinicAccessService
  ) {
    const languageUpdate$ = this.dataBrokerService.subscribe('language-update');
    const createdOrg$ = this.dataBrokerService.subscribe('created-org');
    const updatedOrg$ = this.dataBrokerService.subscribe('organization-updated');

    this.updateLanguage();
    languageUpdate$.pipe(untilDestroyed(this)).subscribe(this.updateLanguage.bind(this));

    createdOrg$.pipe(untilDestroyed(this)).subscribe(this.onClose.bind(this));
    updatedOrg$.pipe(untilDestroyed(this)).subscribe(this.onClose.bind(this));
  }

  ngOnInit() {

    this.tabList = ['Clinic Details'];
    if (this.data.mode === 'edit') {
      this.isNewOrg = false;
      this.getAccess();
    }
    this.getOrgInfo();
    if (this.isNewOrg) {
      this.createForm();
    }

    this.getOrganizationInfo();
    this.updateFormValidation();
  }

  createForm() {
    this.clinicGroup = this.fb.group({
      id: [{value: '', disabled: true}],
      name: ['', [Validators.required, Validators.maxLength(100)]],
      icon: [''],
      address: this.fb.group({
        address1: ['', Validators.required],
        city: ['', Validators.required],
        state: ['', Validators.required],
        zipcode: [
          '',
          [Validators.required, Validators.pattern('^[0-9]*$'), Validators.minLength(5), Validators.maxLength(5)],
        ],
      }),
      ledInfo: this.fb.group({
        ledType: [],
      }),
      employerIdNumber: [''],
      enableWgAPI: [false],
      date: [''],
      startTime: [''],
      endTime: [''],
      contactName: ['', [Validators.required, Validators.pattern(config.NAME_PATTERN)]],
      contactPhoneNumber: ['', FormCustomValidators.phoneValidatorNotRequired],
      contactEmail: ['', [Validators.required, Validators.pattern(this.regex)]],
      backUpContactName: ['', Validators.pattern(config.NAME_PATTERN)],
      backUpContactPhoneNumber: ['', FormCustomValidators.phoneValidatorNotRequired],
      backUpContactEmail: ['', Validators.pattern(this.regex)],
      billingContactEmail: ['', [Validators.required, Validators.pattern(this.regex)]],
      billingContactFn: ['', Validators.required],
      billingContactLn: ['', Validators.required],
      billingContactPhone: ['', FormCustomValidators.phoneValidator],
      businessPK: ['', Validators.required],
      contactPhone: ['', FormCustomValidators.phoneValidator],
      autoScheduleRoundTwo: [true],
      completeProfile: [true],
      insurance: [true],
      assessment: [true],
      consent: [true],
      businessLicense: [''],
      shortName: [''],
      podType: ['Pod'],
      orgTimezone: ['', Validators.required],
      primaryStore: [''],
      supportingStoreOne: [''],
      supportingStoreTwo: [''],
    });

    if (!this.isNewOrg) {
      this.patchOrgForm();
    } else {
      this.isLoaded = true;
    }
  }

  private patchOrgForm() {
    const {
      id,
      name,
      employerIdNumber,
      shortName,
      organizationImage,
      address,
      enableWgAPI,
      podType,
      orgTimezone,
      billingContactEmail,
      billingContactFn,
      billingContactLn,
      billingContactPhone,
      businessPK,
      contactPhone,
      primaryStore,
      supportingStoreOne,
      supportingStoreTwo,
      contactName,
      contactEmail,
    } = this.organization;
    let extraSettings;
    if (this.organization.extraSettings) {
      extraSettings = JSON.parse(this.organization.extraSettings);
    }

    this.clinicGroup.patchValue({
      id,
      name,
      employerIdNumber,
      locationType: 'Organization',
      shortName,
      icon: organizationImage,
      address: new Address(address),
      enableWgAPI,
      podType,
      orgTimezone,
      billingContactEmail,
      billingContactFn,
      billingContactLn,
      billingContactPhone,
      businessPK,
      contactPhone,
      primaryStore,
      supportingStoreOne,
      supportingStoreTwo,
      autoScheduleRoundTwo: extraSettings?.autoScheduleRoundTwo,
      // type_optional: extraSettings?.type_optional,
      // type_walk_ins: extraSettings?.type_walk_ins,
      // type_external_provider_access: extraSettings?.type_external_provider_access,
      completeProfile: extraSettings?.completeProfile,
      insurance: extraSettings?.insurance,
      assessment: extraSettings?.assessment,
      consent: extraSettings?.consent,
      //contactName: extraSettings?.contactName,
      contactPhoneNumber: extraSettings?.contactPhoneNumber,
      //contactEmail: extraSettings?.contactEmail,
      backUpContactName: extraSettings?.backUpContactName,
      backUpContactPhoneNumber: extraSettings?.backUpContactPhoneNumber,
      backUpContactEmail: extraSettings?.backUpContactEmail,
      contactName,
      contactEmail,
    });

    this.isLoaded = true;
  }

  private getOrgInfo() {
    if (this.data.mode === 'edit') {
      // to allow edit multiple times without refresh page
      this.orgOverviewService.clearOrgOverview();
      this.orgOverviewService
        .getOrgOverview()
        .pipe(mergeMap((organization) => this.imageService.addImage(organization, 'organizationImage', 150)))
        .subscribe((org: OrganizationOverview) => {
          this.organization = org;
          this.isLoaded = true;
          this.createForm();
          this.updateFormValidation();
          //this.clinicEditAccess();
        });
    } else {
      this.isLoaded = true;
    }
  }

  public selectTab(index: number) {
    this.selectedTab = index;
  }

  complete() {
    this.isSubmitted = true;
    this.updateFormValidation();
    this.podType = this.clinicGroup.get('podType').value;
    if (this.clinicGroup.valid) {
      this.isLoading = true;
      const {address1, city, state, zipcode} = this.clinicGroup.value.address;
      const address = `${address1}, ${city}, ${state} ${zipcode}`;
      new google.maps.Geocoder().geocode({address, componentRestrictions: {country: 'USA'}}, (results = [], status) => {
        this.isLoading = false;
        if ([google.maps.GeocoderStatus.OK, google.maps.GeocoderStatus.ZERO_RESULTS].includes(status)) {
          this.ngZone.run(() => {
            this.checkGoogleAddressService
              .checkGeocoderResults(results, this.clinicGroup.value.address, [
                'premise',
                'subpremise',
                'street_address',
              ])
              .subscribe((res) => {
                if (res) {
                  this.address = res;
                  this.address.country = 'US';
                  this.save();
                }
              });
          });
        } else {
          this.clinicGroup.get('address').setErrors({notExist: true});
        }
      });
    }
  }

  save() {
    const isSuperAdmin = +this.session.sessionAccount.privilege.id === globals.PRIVILEGEID.SUPER_ADMIN;
    const {
      name,
      employerIdNumber,
      shortName,
      enableWgAPI,
      podType,
      orgTimezone,
      billingContactEmail,
      billingContactFn,
      billingContactLn,
      billingContactPhone,
      businessPK,
      contactPhone,
      contactEmail,
      contactName,
      primaryStore,
      supportingStoreOne,
      supportingStoreTwo,
      ledInfo: {ledType = null, ledFirstName = null, ledLastName = null, ledNpi = null},
    } = this.clinicGroup.value;
    const extraSettings = {
      autoScheduleRoundTwo: this.clinicGroup.value.autoScheduleRoundTwo,
      // type_optional: this.clinicGroup.value.type_optional,
      // type_walk_ins: this.clinicGroup.value.type_walk_ins,
      // type_external_provider_access: this.clinicGroup.value.type_external_provider_access,
      completeProfile: this.clinicGroup.value.completeProfile,
      insurance: this.clinicGroup.value.insurance,
      assessment: this.clinicGroup.value.assessment,
      consent: this.clinicGroup.value.consent,
      contactName: this.clinicGroup.value.contactName,
      contactPhoneNumber: this.clinicGroup.value.contactPhoneNumber,
      contactEmail: this.clinicGroup.value.contactEmail,
      backUpContactName: this.clinicGroup.value.backUpContactName,
      backUpContactPhoneNumber: this.clinicGroup.value.backUpContactPhoneNumber,
      backUpContactEmail: this.clinicGroup.value.backUpContactEmail,
    };
    const {id: orgId} = this.clinicGroup.getRawValue();

    let {icon} = this.clinicGroup.value;

    if (!icon && this.organization && this.organization.organizationImage?.length) {
      icon = 'DELETE';
    }

    const body: Organization = {
      address: this.address,
      name,
      employerIdNumber,
      image: icon ? icon : null,
      type: this.organization?.type || this.data.organizationType,
      enableWgAPI,
      podType,
      orgTimezone,
      billingContactEmail,
      billingContactFn,
      billingContactLn,
      billingContactPhone,
      businessPK,
      contactPhone,
      contactEmail,
      contactName,
      primaryStore,
      supportingStoreOne,
      supportingStoreTwo,
      extraSettings: JSON.stringify(extraSettings),
      ledType,
      ledFirstName,
      ledLastName,
      ledNpi,
    };
    if (isSuperAdmin) body.shortName = shortName;
    if (this.isNewOrg) {
      // Create  organization
      body.parentId = this.data.parentOrgId;

      this.snackBar.openFromComponent(SnackbarComponent, {
        ...this.snackBarConf,
        data: 'Creating organization',
      });
      this.orgService.createOrganization(body).subscribe(
        (organization) => {
          this.snackBar.open('Created Successfully!', '', {
            ...this.snackBarConf,
            duration: 2000,
          });
          if(this.data.doNotRedirectToClinicOverviewAfterAdd){
            this.organizationService.addFacilityAppointment({ orgId: this.data?.parentOrgId, clinicId: organization?.id }).subscribe({
              next: () => {
                this.snackBar.open('School has been attached to patient group', '', { ...this.snackBarConf, duration: 2000 });
                this.organization = organization as OrganizationOverview;
                this.orgOverviewService.addedClinic(organization);
                this.onClose(organization);
              }
            });
          } else {
            this.organization = organization as OrganizationOverview;
            this.orgOverviewService.addedClinic(organization);
            this.onClose(organization);
            localStorage.setItem('organization-id', organization.id);
            this.router.navigate([`/curapatient/organizations/organization/clinic`]).then();
          }
        },
        (error: HttpErrorResponse) => {
          if (
            error.error &&
            ((error.error.message && error.error.message.includes('exist')) ||
              (error.error.debugMessage && error.error.debugMessage.includes('exist')))
          ) {
            this.snackBar.open(`Clinic name "${name}" already exist`, '', {
              ...this.snackBarConf,
              duration: 3000,
            });
          } else {
            this.snackBar.open('Creating Failure!', '', {
              ...this.snackBarConf,
              duration: 3000,
            });
          }
        }
      );
    } else {
      // Update organization
      body.id = orgId;
      body.address.id = this.organization.address.id;
      body.address.name = this.organization.address.name;

      this.snackBar.openFromComponent(SnackbarComponent, {
        ...this.snackBarConf,
        data: 'Updating organization',
      });

      this.orgService.updateOrganization(body).subscribe(
        (organization) => {
          this.snackBar.open('Updated Successfully!', '', {
            ...this.snackBarConf,
            duration: 2000,
          });
          // patch current parent organization
          if (!organization.parents) {
            organization.parents = this.organization.parents;
          }
          this.organization = organization as OrganizationOverview;
          this.onClose();
          this.dataBrokerService.publish(this.organization, 'organization-updated');
        },
        (error: HttpErrorResponse) => {
          if (error.error && error.error.message && error.error.message.includes('exist')) {
            this.snackBar.open(error.error.message, '', {
              ...this.snackBarConf,
              duration: 3000,
            });
          } else {
            this.snackBar.open('Updating Failure!', '', {
              ...this.snackBarConf,
              duration: 3000,
            });
          }
        }
      );
    }
  }

  onClose(value?: any): void {
    this.dialogRef.close(value);
  }

  checkAndReturnStoreVal(isPrimaryStoreNumVal){
    this.checkPrimaryStoreNumber = !!isPrimaryStoreNumVal;
  }

  private updateLanguage() {
    this.labels = {
      title: '',
      createBtnLabel: this.languageService.language.org_edit_createBtnLabel,
      saveBtnLabel: this.languageService.language.org_edit_saveBtnLabel,
    };
  }

  updateFormValidation() {
    const businessPK = this.clinicGroup?.get('businessPK');
    const contactname = this.clinicGroup?.get('contactName');
    const contactemail = this.clinicGroup?.get('contactEmail');
    const contactphone = this.clinicGroup?.get('contactPhone');
    const backUpContactName = this.clinicGroup?.get('backUpContactName');
    const backUpContactPhoneNumber = this.clinicGroup?.get('backUpContactPhoneNumber');
    const backUpContactEmail = this.clinicGroup?.get('backUpContactEmail');
    const primaryStore = this.clinicGroup?.get('primaryStore');

    // Edit MVC Clinic validation
    let podType = this.clinicGroup?.get('podType').value;
    if (podType == 'Mobile Vaccine Service') {
      businessPK.clearValidators();
      contactname.clearValidators();
      contactemail.clearValidators();
      contactphone.clearValidators();
      backUpContactName.clearValidators();
      backUpContactPhoneNumber.clearValidators();
      backUpContactEmail.clearValidators();
      primaryStore.clearValidators();
      //      enableWgAPI.setValue(false);
    }

    // On change pod type update field validation
    this.clinicGroup?.get('podType').valueChanges.subscribe((mode: string) => {
      if (mode == 'Mobile Vaccine Service') {
        businessPK.clearValidators();
        contactname.clearValidators();
        contactemail.clearValidators();
        contactphone.clearValidators();
        backUpContactName.clearValidators();
        backUpContactPhoneNumber.clearValidators();
        backUpContactEmail.clearValidators();
        primaryStore.clearValidators();
      } else {
        businessPK.setValidators([Validators.required]);
        contactname.setValidators([Validators.required, Validators.pattern(config.NAME_PATTERN)]);
        contactemail.setValidators([Validators.required, Validators.pattern(this.regex)]);
        contactphone.setValidators([FormCustomValidators.phoneValidator]);
        backUpContactName.setValidators([Validators.pattern(config.NAME_PATTERN)]);
        backUpContactPhoneNumber.setValidators([FormCustomValidators.phoneValidatorNotRequired]);
        backUpContactEmail.setValidators([Validators.pattern(this.regex)]);
        if (this.data.parentOrgId === environment.walgreenHeadquarterId) {
          primaryStore.setValidators([Validators.required]);
        }
      }
      businessPK.updateValueAndValidity();
      contactname.updateValueAndValidity();
      contactemail.updateValueAndValidity();
      contactphone.updateValueAndValidity();
      backUpContactName.updateValueAndValidity();
      backUpContactPhoneNumber.updateValueAndValidity();
      backUpContactEmail.updateValueAndValidity();
      primaryStore.updateValueAndValidity();
    });
  }

  private getOrganizationInfo() {
    this.orgOverviewService
      .getOrgOverview()
      .pipe(mergeMap((organization) => this.imageService.addImage(organization, 'organizationImage', 150)))
      .subscribe((parentOrg: OrganizationOverview) => {
        this.getOrgName(parentOrg);
      });
  }

  getOrgName(parentOrg) {
    let podType = this.clinicGroup.get('podType').value;
    const primaryStore = this.clinicGroup.get('primaryStore');
    const supportingStoreOne = this.clinicGroup.get('supportingStoreOne');
    const supportingStoreTwo = this.clinicGroup.get('supportingStoreTwo');

    if (this.data.mode === 'edit') {
      parentOrg.parents.content.forEach((item) => {
        if (item.id === environment.walgreenHeadquarterId) {
          this.orgId = item.id;
        }
      });
    } else {
      this.orgId = parentOrg.id;
    }

    if (this.orgId === environment.walgreenHeadquarterId && podType !='Mobile Vaccine Service') {
      primaryStore.setValidators([Validators.required, Validators.minLength(5)]);
      supportingStoreOne.setValidators([Validators.minLength(5)]);
      supportingStoreTwo.setValidators([Validators.minLength(5)]);
      primaryStore.updateValueAndValidity();
    }
  }

  getAccess(){
    this.isEditable = this.clinicAccessService.getAccess();
  }
}
